import React, { useState } from 'react';
import { formatLocalDate } from '../utils/dateUtils';

interface DateCalendarProps {
  selectedDates: string[];
  onDateChange: (date: string, selected: boolean) => void;
}

const DateCalendar: React.FC<DateCalendarProps> = ({ selectedDates, onDateChange }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  
  const today = new Date();

  const formatDate = (date: Date) => {
    // Ensure date formatting is based on local time (Vancouver time zone) to avoid date offset caused by UTC conversion
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const isDateSelected = (date: Date) => {
    return selectedDates.includes(formatDate(date));
  };

  const handleDateClick = (date: Date) => {
    const dateStr = formatDate(date);
    const isSelected = isDateSelected(date);
    onDateChange(dateStr, !isSelected);
  };

  // Get all dates in the current month
  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const startDayOfWeek = firstDay.getDay();

    const days = [];

    // Add empty cells (for week alignment)
    for (let i = 0; i < startDayOfWeek; i++) {
      days.push({ date: null, isCurrentMonth: false, isEmpty: true });
    }

    // Add dates for the current month
    for (let day = 1; day <= daysInMonth; day++) {
      const currentDate = new Date(year, month, day);
      days.push({ date: currentDate, isCurrentMonth: true, isEmpty: false });
    }

    return days;
  };

  const getMonthName = (date: Date) => {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return months[date.getMonth()];
  };

  const goToPreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const isDateSelectable = (date: Date) => {
    // Compare date part, allow selection of today and future dates
    const dateStr = formatDate(date); // "2025-08-16"
    const todayStr = formatDate(today); // "2025-08-16"
    return dateStr >= todayStr; // Allow selection of today and later
  };

  const days = getDaysInMonth(currentDate);

  return (
    <div className="bg-white border border-gray-300 rounded-lg shadow-sm p-3 md:p-6 flex flex-col md:flex-row gap-4 md:gap-6 max-w-4xl">
      {/* Left side: Calendar */}
      <div className="flex-shrink-0 w-full md:w-auto">
        {/* Month navigation */}
        <div className="flex items-center justify-between mb-4 md:mb-6">
          <button
            type="button"
            onClick={goToPreviousMonth}
            className="p-2 md:p-2 hover:bg-gray-100 rounded-md text-gray-600 min-h-[44px] min-w-[44px] flex items-center justify-center"
          >
            ←
          </button>
          <h3 className="text-lg md:text-xl font-medium text-gray-800">
            {getMonthName(currentDate)} {currentDate.getFullYear()}
          </h3>
          <button
            type="button"
            onClick={goToNextMonth}
            className="p-2 md:p-2 hover:bg-gray-100 rounded-md text-gray-600 min-h-[44px] min-w-[44px] flex items-center justify-center"
          >
            →
          </button>
        </div>

        {/* Day of week headers */}
        <div className="grid grid-cols-7 gap-1 mb-2 md:mb-3">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="text-center text-sm md:text-base font-medium text-gray-500 py-2 md:py-3 w-10 md:w-12">
              {day}
            </div>
          ))}
        </div>

        {/* Date grid */}
        <div className="grid grid-cols-7 gap-1">
          {days.map((day, index) => {
            if (day.isEmpty || !day.date) {
              return <div key={index} className="h-10 w-10 md:h-12 md:w-12"></div>;
            }

            const date = day.date;
            const isSelected = isDateSelected(date);
            const isToday = date.toDateString() === today.toDateString();
            const isSelectable = isDateSelectable(date);

            return (
              <button
                key={index}
                type="button"
                onClick={() => isSelectable && handleDateClick(date)}
                disabled={!isSelectable}
                className={`
                  h-10 w-10 md:h-12 md:w-12 rounded-md text-sm md:text-base transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center
                  ${!day.isCurrentMonth 
                    ? 'text-gray-300 cursor-not-allowed' 
                    : isSelectable
                      ? isSelected 
                        ? 'bg-blue-600 text-white hover:bg-blue-700 font-medium' 
                        : 'text-gray-700 hover:bg-gray-100'
                      : 'text-gray-300 cursor-not-allowed'
                  }
                  ${isToday && day.isCurrentMonth ? 'ring-2 ring-blue-300' : ''}
                  focus:outline-none focus:ring-2 focus:ring-blue-500
                `}
              >
                {date.getDate()}
              </button>
            );
          })}
        </div>
      </div>

      {/* Right side: Selected dates */}
      <div className="flex-1 min-w-0 w-full md:w-auto">
        <div className="p-3 md:p-4 bg-blue-50 rounded-md h-full flex flex-col">
          <p className="text-sm md:text-base text-blue-800 mb-3 md:mb-4 font-medium">
            {selectedDates.length} dates selected
          </p>
          
          <div className="flex-1">
            {selectedDates.length > 0 ? (
              <div className="space-y-2 md:space-y-3 max-h-48 md:max-h-64 overflow-y-auto">
                {selectedDates.map(date => (
                  <div
                    key={date}
                    className="group flex items-center justify-between px-3 md:px-4 py-2 md:py-3 bg-blue-100 text-blue-800 text-sm md:text-base rounded-md hover:bg-blue-200 transition-colors min-h-[44px]"
                  >
                    <span className="font-medium text-xs md:text-sm">
                      {formatLocalDate(date, 'en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        weekday: 'short'
                      })}
                    </span>
                    <button
                      type="button"
                      onClick={() => onDateChange(date, false)}
                      className="ml-2 text-blue-600 hover:text-blue-800 font-medium w-8 h-8 md:w-6 md:h-6 flex items-center justify-center rounded hover:bg-blue-300 transition-all opacity-100 md:opacity-0 md:group-hover:opacity-100 min-h-[44px] min-w-[44px] md:min-h-[24px] md:min-w-[24px]"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-blue-600 text-xs md:text-sm">
                Click on calendar to select dates
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DateCalendar;